import './Login.scss';
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useAuth } from '../contexts/auth';
import { useFullScreenLoader } from '../contexts/fullScreenLoader';
import Cookies from 'js-cookie';
import { Navigate, useLocation } from 'react-router-dom';
import Logo from '../components/common/Logo';
import { useKeycloak } from '@react-keycloak/web';
import { UserStatus } from '../api/ControlPanelManager';
import { Flex, Modal } from 'antd';
import AppLoader from '../components/common/AppLoader';
import classNames from 'classnames';
import { Authentication } from '../api/Authentication';
import { useToasts } from '../contexts/toasts';

const loginSubtitle = window.__env.LOGIN_SUBTITLE || "Provide your credentials to sign in.";

export default function Login() {
  const location = useLocation();
  const { token, userChecked, setToken, setUserChecked } = useAuth();
  const [showUserModal, setShowUserModal] = useState(false);
  const [userStatus, setUserStatus] = useState<UserStatus>();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();
  const { initialized, keycloak } = useKeycloak();

  const checkUser = useCallback(async () => {
    try {
      const { data: status } = await Authentication.getUserStatus()
      if (
        !status.keycloak.attributes || !status.keycloak.organizations ||
        !status.keycloak.permissions || !status.catalogue || !status.minio
      ) {
        console.log("User not well-configured, setting it up...");
        setShowUserModal(true);
        setUserStatus(status);

        const { data } = await Authentication.fixUser();
        console.log("User fixed", data);
        setShowUserModal(false);
      }
      hideLoader();
      console.log("User status is good, proceeding...");
      Cookies.set(window.__env.AUTH_USER_CHECKED_NAME || "alida-user-checked", "true");
      setUserChecked("true");
    } catch (err: any) {
      console.log('An error occurred while retrieving user status', err);
      showToast('user-status-error', 'An error occurred during user status check', 'User error', 'danger');
      Cookies.remove(window.__env.AUTH_TOKEN_NAME || "alida-token");
      Cookies.remove(window.__env.AUTH_REFRESH_TOKEN_NAME || "alida-refresh-token");
      Cookies.remove(window.__env.AUTH_USER_NAME || "alida-user");
      Cookies.remove(window.__env.AUTH_USER_CHECKED_NAME || "alida-user-checked");
      keycloak.logout({ redirectUri: window.__env.POST_LOGOUT_REDIRECT_URI });
    }
  }, [hideLoader, showToast, setUserChecked, keycloak]);

  useEffect(() => {
    if (location.hash) showLoader();

    if (keycloak.authenticated) {
      // TODO: set expires based on response (in model and application creation too)
      Cookies.set(window.__env.AUTH_TOKEN_NAME || "alida-token", keycloak.token!);
      Cookies.set(window.__env.AUTH_REFRESH_TOKEN_NAME || "alida-refresh-token", keycloak.refreshToken!);
      setToken(keycloak.token);
      // checking user AFTER setting token
      if (!userChecked) checkUser();
    }

    // hides loader before changing page
    return () => hideLoader();
  }, [location.hash, keycloak?.token, keycloak?.refreshToken, keycloak?.authenticated, userChecked, showLoader, hideLoader, setToken, checkUser])

  const login = useCallback(() => {
    keycloak?.login();
  }, [keycloak]);

  if (token && userChecked) return <Navigate to={window.__env.AFTER_LOGIN_ROUTE || "/dashboard"} />;

  return (
    <Container className="login-container">
      <Card>
        <Card.Body>
          <div className="text-center mb-5">
            <Logo width={300} className="my-4" />
            {/* <Card.Title>Login</Card.Title> */}
            {loginSubtitle?.length > 0 && <Card.Subtitle className="text-muted">{loginSubtitle}</Card.Subtitle>}
            {/* <Card.Subtitle className="text-muted">Push the button below to sign in.</Card.Subtitle> */}
          </div>

          <Row className="justify-content-center">
            <Col sm={8} md={5}>
              <Button size="sm" className="w-100" disabled={!initialized} onClick={login}>
                <FontAwesomeIcon icon={solid('sign-in')} />
                <span className="ms-2">Login</span>
              </Button>

              <div className="mt-3 text-center">
                {initialized
                  ? (
                    <>
                      <FontAwesomeIcon className="text-success" icon={solid('circle')} />
                      <small className="ms-2">Connected to Keycloak</small>
                    </>
                  )
                  : (
                    <>
                      <Spinner size="sm" />
                      <small className="ms-2">Connecting to Identity Manager...</small>
                    </>
                  )}
              </div>
            </Col>
            {/* <Col xs={12} className="mt-3 text-center">
              <small className="text-muted">
                If you do not have an account, please apply sending a mail to <a href="mailto:info@alidalab.it">info@alidalab.it</a>
              </small>
            </Col> */}
          </Row>
        </Card.Body>
      </Card>

      <Modal
        // title="Basic Modal"
        closable={false}
        maskClosable={false}
        width={600}
        open={showUserModal}
        footer={false}
      >
        <p className="mb-2">Your account is not well-configured. Following items are currently being fixed:</p>
        <div className="mb-3">
          <UserStatusItem status={userStatus?.keycloak.organizations}>User organizations</UserStatusItem>
          <UserStatusItem status={userStatus?.keycloak.permissions}>User permissions</UserStatusItem>
          <UserStatusItem status={userStatus?.minio}>User secure space</UserStatusItem>
          <UserStatusItem status={userStatus?.catalogue}>User resources</UserStatusItem>
          <UserStatusItem status={userStatus?.keycloak.attributes}>Additional account settings</UserStatusItem>
        </div>
        <Alert className="py-2 d-flex align-items-center" variant="warning">
          <AppLoader className="p-0 d-inline-flex pe-1" size={15} strokeWidth={4} color="currentColor" />
          <span className="ms-2">Operations are in progress. This might take a while...</span>
        </Alert>
      </Modal>
    </Container>
  )
}

type UserStatusItemProps = {
  status?: boolean;
  children: React.ReactNode;
}

function UserStatusItem({ status, children }: UserStatusItemProps) {
  return (
    <Flex align="center">
      <FontAwesomeIcon
        className={classNames("me-2", status ? 'text-success' : 'text-danger')}
        icon={status ? solid('check-circle') : solid('times-circle')}
        size="sm"
        fixedWidth
      />
      {children}
    </Flex>
  )
}